import { Box, Button, Checkbox, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { BoxDetailsFormValues } from "../../../types";
import {
  BOX_COG_MAIN_FIELD,
  BOX_COG_RANGE_FROM_FIELD,
  BOX_COG_RANGE_TO_FIELD,
  BOX_OVERHANG_FIELD,
  BOX_OVERHANG_MAX_FIELD,
  BOX_OVERHANG_PLUS_FIELD,
} from "../data";
import { FormFieldControl } from "../VehicleDetails/FormControls";
import { useStyles } from "./styles";

type BoxFieldProps = {
  formValues: BoxDetailsFormValues;
  formPrefix: string;
  updateField: (field: keyof BoxDetailsFormValues, value: string | boolean) => void;
};

type ActionColumnProps = {
  formPrefix: string;
  buttonKey: string;
};

type ActionButtonsColumnProps = {
  formPrefix: string;
  buttonKeys: [string, string];
};

export const ActionButtonColumn = ({ formPrefix, buttonKey }: ActionColumnProps) => {
  const { t } = useTranslation();
  const styles = useStyles();

  return (
    <Box className={styles.actionColumn}>
      <Typography className={styles.labelSpacer}>&nbsp;</Typography>
      <Button className={styles.btn} variant="outlined" color="primary">
        {t(`${formPrefix}.BUTTONS.${buttonKey}`)}
      </Button>
    </Box>
  );
};

export const ActionButtonsColumn = ({ formPrefix, buttonKeys }: ActionButtonsColumnProps) => {
  const { t } = useTranslation();
  const styles = useStyles();

  return (
    <Box className={styles.actionColumn}>
      <Typography className={styles.labelSpacer}>&nbsp;</Typography>
      <Box className={styles.actionButtons}>
        {buttonKeys.map((buttonKey) => (
          <Button key={buttonKey} className={styles.btn} variant="outlined" color="primary">
            {t(`${formPrefix}.BUTTONS.${buttonKey}`)}
          </Button>
        ))}
      </Box>
    </Box>
  );
};

export const CenterOfGravityFields = ({
  formValues,
  formPrefix,
  updateField,
}: BoxFieldProps) => {
  const { t } = useTranslation();
  const styles = useStyles();

  return (
    <Box className={styles.cogRow}>
      <Box className={styles.cogRangeGroup}>
        <Typography className={styles.cogRangeLabel}>
          {t(BOX_COG_RANGE_FROM_FIELD.labelKey)}
        </Typography>
        <Box className={styles.cogRangeFields}>
          <FormFieldControl
            config={BOX_COG_RANGE_FROM_FIELD}
            value={formValues.centerOfGravityRange}
            onChange={(value) => updateField("centerOfGravityRange", value)}
            hideLabel
            compact
          />
          <Box className={styles.cogSeparator}>
            <Typography className={styles.cogSeparatorText}>
              {t(`${formPrefix}.FIELDS.TO`)}
            </Typography>
          </Box>
          <FormFieldControl
            config={BOX_COG_RANGE_TO_FIELD}
            value={formValues.centerOfGravityTo}
            onChange={(value) => updateField("centerOfGravityTo", value)}
            hideLabel
            compact
          />
        </Box>
      </Box>
      <Box className={styles.cogMainField}>
        <FormFieldControl
          config={BOX_COG_MAIN_FIELD}
          value={formValues.centerOfGravity}
          onChange={(value) => updateField("centerOfGravity", value)}
        />
      </Box>
    </Box>
  );
};

export const RearOverhangFields = ({
  formValues,
  formPrefix,
  updateField,
}: BoxFieldProps) => {
  const { t } = useTranslation();
  const styles = useStyles();

  return (
    <Box className={styles.overhangRow}>
      <FormFieldControl
        config={BOX_OVERHANG_FIELD}
        value={formValues.rearOverhang}
        onChange={(value) => updateField("rearOverhang", value)}
        compact
      />
      <Typography className={styles.overhangSeparator}>+</Typography>
      <FormFieldControl
        config={BOX_OVERHANG_PLUS_FIELD}
        value={formValues.rearOverhangPlus}
        onChange={(value) => updateField("rearOverhangPlus", value)}
        hideLabel
        compact
      />
      <FormFieldControl
        config={BOX_OVERHANG_MAX_FIELD}
        value={formValues.maxRearOverhang}
        onChange={(value) => updateField("maxRearOverhang", value)}
        compact
      />
      <Typography className={styles.overhangStatic}>
        {t(`${formPrefix}.VALUES.OVERHANG_UNIT`)}
      </Typography>
    </Box>
  );
};

export const StructurePanel = ({ formValues, formPrefix, updateField }: BoxFieldProps) => {
  const { t } = useTranslation();
  const styles = useStyles();

  return (
    <Box className={styles.structureRow}>
      <Box className={styles.structurePanel}>
        <Box className={styles.structureCheckboxRow}>
          <Checkbox
            size="small"
            checked={formValues.clampingLocks}
            onChange={(event) => updateField("clampingLocks", event.target.checked)}
            sx={{ p: 0 }}
          />
          <Typography className={styles.fieldValue}>
            {t(`${formPrefix}.FIELDS.CLAMPING_LOCKS`)}
          </Typography>
        </Box>
        <FormFieldControl
          dense
          config={{
            field: "rearDoor",
            labelKey: `${formPrefix}.FIELDS.REAR_DOOR`,
            type: "select",
            options: [
              ["FOUR_UNITS_PLUS_PREP", `${formPrefix}.OPTIONS.REAR_DOOR_FOUR_UNITS_PLUS_PREP`],
            ],
          }}
          value={formValues.rearDoor}
          onChange={(value) => updateField("rearDoor", value)}
        />
      </Box>
      <FormFieldControl
        wrapperClassName={styles.structureCommentField}
        inputClassName={styles.structureCommentInput}
        config={{
          field: "comment",
          labelKey: `${formPrefix}.FIELDS.COMMENT`,
          type: "text",
        }}
        value={formValues.comment}
        onChange={(value) => updateField("comment", value)}
      />
    </Box>
  );
};
